import { getRelatedServicesForPost, type PillarTopic } from "@/data/contentArchitecture";
import { getArabicRouteForEnglishPath, isPublishedArabicRoute } from "@/i18n/localisedRoutes";

/**
 * Resolve a selected English service pillar to a locale-safe destination.
 * Arabic cards disappear when the equivalent Arabic service is not published;
 * they must never silently send readers back to an English service page.
 */
export function resolveRelatedServiceHref(service: PillarTopic, locale: "en" | "ar"): string | undefined {
  if (locale === "en") return service.slug.endsWith("/") ? service.slug : `${service.slug}/`;

  const route = getArabicRouteForEnglishPath(service.slug);
  return isPublishedArabicRoute(route) && route?.equivalent ? route.arPath : undefined;
}

/**
 * English uses the first contextual service. Arabic evaluates the same ordered
 * source candidates first, then keeps the first one with a published Arabic
 * equivalent. This preserves editorial relevance without an English fallback.
 */
export function getLocaleRelatedServicesForPost(slug: string, tags: string[], locale: "en" | "ar"): PillarTopic[] {
  const candidates = getRelatedServicesForPost(slug, tags);
  return candidates
    .filter((service) => Boolean(resolveRelatedServiceHref(service, locale)))
    .slice(0, 1);
}

/**
 * Select one contextual service that can be published consistently in both
 * languages. Arabic availability is part of selection, never a fallback after
 * an English service card has already been chosen.
 */
export function getBilingualRelatedServicesForPost(slug: string, tags: string[]): PillarTopic[] {
  return getLocaleRelatedServicesForPost(slug, tags, "ar");
}
